import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { CloudProvider } from '../../shared/src/types/connect';
import {
  MessengerButtons,
  MessengerConnect,
  messengerNames,
  useMessengersAvailable,
} from './MessengerConnect';
import { hasSession, SESSION_KEY } from './session';

/** Sign in by linking a messenger, as when adding another device. */
function SignInCard({ onSignedIn }: { onSignedIn: (token: string) => void }) {
  const available = useMessengersAvailable();
  const messengers = available ?? [];
  const [chosen, setChosen] = useState<CloudProvider | null>(null);
  return (
    <section className="sync-auth-card">
      <span className="sync-eyebrow">Your SoulThread</span>
      <h1 id="sign-in-title">A little closer to you.</h1>
      {chosen ? (
        <MessengerConnect
          provider={chosen}
          onSignedIn={onSignedIn}
          onClose={() => setChosen(null)}
        />
      ) : available === null ? (
        <p className="sync-muted">Just a moment…</p>
      ) : messengers.length > 0 ? (
        <>
          <p>
            Link {messengerNames(messengers)} the way you would a computer, and your conversations
            come with it.
          </p>
          <MessengerButtons providers={messengers} verb="Continue with" onChoose={setChosen} />
        </>
      ) : (
        <p role="status">Messenger sign-in is temporarily unavailable. Please try again shortly.</p>
      )}
    </section>
  );
}

/** The sign-in card over whatever page it was opened from. */
function SignInDialog({
  onSignedIn,
  onClose,
}: {
  onSignedIn: (token: string) => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      // Tab stays inside the dialog.
      if (event.key !== 'Tab' || !dialog.current) return;
      const focusable = dialog.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="sign-in-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className="sign-in-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-in-title"
      >
        <button className="sign-in-close" type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <SignInCard onSignedIn={onSignedIn} />
      </div>
    </div>
  );
}

const SignInContext = createContext<(next?: string) => void>(() => undefined);

/**
 * Signing in opens over the current page and, once signed in, goes where the person was headed.
 * The app sends people who aren't signed in here with `?sign-in=<where they were going>`.
 */
export function SignInProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [opened, setOpened] = useState<string | null>(null);
  const requested = new URLSearchParams(location.search).get('sign-in');
  // Only paths within the app, so the link can't send anyone elsewhere.
  const next = (candidate: string | null) =>
    candidate && /^\/app(?:[/?]|$)/.test(candidate) ? candidate : '/app';
  const destination = opened ?? (requested !== null ? next(requested) : null);

  const open = useCallback(
    (to?: string) => {
      if (hasSession()) navigate(next(to ?? null));
      else setOpened(next(to ?? null));
    },
    [navigate]
  );
  const close = useCallback(() => {
    setOpened(null);
    if (requested !== null) navigate(location.pathname, { replace: true });
  }, [location.pathname, navigate, requested]);

  return (
    <SignInContext.Provider value={open}>
      {children}
      {destination && (
        <SignInDialog
          onClose={close}
          onSignedIn={(token) => {
            localStorage.setItem(SESSION_KEY, token);
            setOpened(null);
            navigate(destination, { replace: requested !== null });
          }}
        />
      )}
    </SignInContext.Provider>
  );
}

/** A way into the app: straight in when signed in, or signing in over this page first. */
export function OpenAppLink({
  children,
  className,
  to = '/app',
}: {
  children: React.ReactNode;
  className?: string;
  to?: string;
}) {
  const open = useContext(SignInContext);
  return (
    <Link
      to={to}
      className={className}
      onClick={(event) => {
        if (hasSession()) return;
        event.preventDefault();
        open(to);
      }}
    >
      {children}
    </Link>
  );
}
